import { useEffect, useState } from "react";
import { Phone, Mail, Briefcase, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import CaseStagePipeline from "@/components/CaseStagePipeline";

/**
 * Card shown at the top of a claimant's case-detail dialog. Displays the
 * assigned attorney's name + firm + contact + current case stage pipeline.
 */
export default function MyCaseAttorneyCard({ caseId }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/me/cases/${caseId}/attorney`)
      .then((r) => mounted && setData(r.data))
      .catch(() => mounted && setData({ assigned: false }))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [caseId]);

  if (loading || !data) return null;

  if (!data.assigned) {
    return (
      <div
        className="card-soft p-4 text-sm text-[#5C6B68]"
        data-testid="my-case-attorney-pending"
      >
        <div className="inline-flex items-center gap-2 font-bold text-[#11322A]">
          <Briefcase className="h-4 w-4" /> {t("mc.attorney.pending_title")}
        </div>
        <p className="mt-1 text-xs">{t("mc.attorney.pending_body")}</p>
      </div>
    );
  }

  const att = data.attorney;
  return (
    <div className="card-soft p-5" data-testid="my-case-attorney">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="label-cap text-[10px]">{t("mc.attorney.your_attorney")}</div>
          <div className="mt-1 font-display text-xl font-black text-[#11322A]" data-testid="my-case-att-name">
            {att.name}
          </div>
          {att.firm_name && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#5C6B68]">
              <Building2 className="h-3 w-3" /> {att.firm_name}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {att.phone && (
              <a
                href={`tel:${att.phone}`}
                className="inline-flex items-center gap-1 font-bold text-[#11322A] hover:text-[#E85D04]"
                data-testid="my-case-att-phone"
              >
                <Phone className="h-3 w-3" /> {att.phone}
              </a>
            )}
            {att.email && (
              <a
                href={`mailto:${att.email}`}
                className="inline-flex items-center gap-1 font-bold text-[#11322A] hover:text-[#E85D04]"
                data-testid="my-case-att-email"
              >
                <Mail className="h-3 w-3" /> {att.email}
              </a>
            )}
          </div>
        </div>
        <div className="rounded-full border border-[#bce5cc] bg-[#E6F6ED] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#166534]">
          {t("mc.attorney.matched")}
        </div>
      </div>

      <div className="mt-5">
        <div className="label-cap text-[10px]">{t("mc.attorney.current_stage")}</div>
        <div className="mt-3">
          <CaseStagePipeline current={data.case_stage || "intake"} />
        </div>
      </div>
    </div>
  );
}
